import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { lookupChiptuning, getChiptuningStages, type ChiptuningResult, type EngineVariant } from '@/api/chiptuningService';

const SIDECODE_PATTERNS: [RegExp, number[]][] = [
  [/^[A-Z]{2}\d{4}$/,        [2, 4]],
  [/^\d{4}[A-Z]{2}$/,        [2, 4]],
  [/^\d{2}[A-Z]{2}\d{2}$/,   [2, 4]],
  [/^[A-Z]{2}\d{2}[A-Z]{2}$/,[2, 4]],
  [/^[A-Z]{4}\d{2}$/,        [2, 4]],
  [/^\d{2}[A-Z]{4}$/,        [2, 4]],
  [/^\d{2}[A-Z]{3}\d$/,      [2, 5]],
  [/^\d[A-Z]{3}\d{2}$/,      [1, 4]],
  [/^[A-Z]{2}\d{3}[A-Z]$/,   [2, 5]],
  [/^[A-Z]\d{3}[A-Z]{2}$/,   [1, 4]],
  [/^[A-Z]{3}\d{2}[A-Z]$/,   [3, 5]],
  [/^[A-Z]\d{2}[A-Z]{3}$/,   [1, 3]],
  [/^\d[A-Z]{2}\d{3}$/,      [1, 3]],
  [/^\d{3}[A-Z]{2}\d$/,      [3, 5]],
]

const formatLicensePlate = (value: string): string => {
  const cleaned = value.replace(/[^A-Z0-9]/g, '').toUpperCase()
  if (cleaned.length < 5) return cleaned
  for (const [pattern, [s1, s2]] of SIDECODE_PATTERNS) {
    if (pattern.test(cleaned)) {
      return `${cleaned.slice(0, s1)}-${cleaned.slice(s1, s2)}-${cleaned.slice(s2)}`
    }
  }
  return cleaned
}

const ChiptuningLookup = () => {
  const [plate, setPlate] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<ChiptuningResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedVariant, setSelectedVariant] = useState<EngineVariant | null>(null)
  const [selectedStageIndex, setSelectedStageIndex] = useState(0)
  const [contactOpen, setContactOpen] = useState(false)
  const [contactForm, setContactForm] = useState({ name: '', phone: '', email: '', message: '' })
  const [contactSubmitting, setContactSubmitting] = useState(false)
  const [contactSuccess, setContactSuccess] = useState(false)

  useEffect(() => {
    if (contactOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [contactOpen])

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!contactForm.name.trim() || !contactForm.phone.trim()) return
    setContactSubmitting(true)
    try {
      const supabaseUrl = 'https://lozejxkhsvwkksgzhrfd.supabase.co'
      const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxvemVqeGtoc3Z3a2tzZ3pocmZkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY0OTY4OTcsImV4cCI6MjA4MjA3Mjg5N30.GvcNibeMI-dF1dq4s4mq5Ap9Edspot9s5mdjUEWfZ0M'
      const currentStg = result?.stages[selectedStageIndex]
      const autoMessage = `Chiptuning aanvraag: ${result?.vehicle.brand} ${result?.vehicle.model} (${result?.vehicle.licensePlate}) — ${currentStg?.stage}: ${selectedVariant?.originalHp}pk → ${currentStg?.tunedHp}pk`
      const fullMessage = contactForm.message.trim()
        ? `${autoMessage}\n\nBericht: ${contactForm.message.trim()}`
        : autoMessage

      const response = await fetch(`${supabaseUrl}/functions/v1/send-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
        body: JSON.stringify({
          name: contactForm.name.trim(),
          phone: contactForm.phone.trim(),
          email: contactForm.email.trim(),
          licensePlate: result?.vehicle.licensePlate || '',
          message: fullMessage,
        }),
      })
      const responseText = await response.text()
      const parsed = responseText.trim() ? JSON.parse(responseText) : null
      if (!response.ok || !parsed?.success) throw new Error(parsed?.error || 'Server error')
      setContactSuccess(true)
    } catch {
      alert('Er ging iets mis. Probeer het opnieuw.')
    } finally {
      setContactSubmitting(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').replace(/-/g, '')
    if (raw.length <= 7) setPlate(formatLicensePlate(raw))
  }

  const handleLookup = async () => {
    if (plate.replace(/-/g, '').length < 5) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const data = await lookupChiptuning(plate)
      setResult(data)
      setSelectedVariant(data.selectedVariant)
      setSelectedStageIndex(0)
    } catch (err: any) {
      setError(err.message || 'Er is iets misgegaan')
    } finally {
      setLoading(false)
    }
  }

  const handleVariantSelect = async (variant: EngineVariant) => {
    setSelectedVariant(variant)
    setSelectedStageIndex(0)
    const stages = await getChiptuningStages(variant.id)
    setResult(prev => prev ? { ...prev, selectedVariant: variant, stages } : null)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleLookup()
  }

  const currentStage = result?.stages[selectedStageIndex]

  return (
    <section className="w-full bg-[#f2f2f0] py-20 md:py-28 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-8 md:px-12 lg:px-16">

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 md:gap-6 mb-12 md:mb-16 lg:mb-20"
        >
          <div>
            <p
              className="text-black text-[11px] uppercase tracking-[0.2em] font-medium mb-4"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Chiptuning calculator
            </p>
            <h2
              className="text-black text-3xl md:text-4xl lg:text-[2.75rem] font-medium tracking-[0.08em] leading-[1.1]"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              ONTDEK UW<br />
              <span className="relative inline-block mt-2">
                <span className="absolute inset-0 bg-[#B1A06D]" style={{ transform: 'skewX(-4deg) rotate(-2deg)', borderRadius: '4px' }} />
                <span className="relative z-10 text-black font-semibold px-3 py-0.5 inline-block">VERMOGENSWINST</span>
              </span>
            </h2>
          </div>
          <p
            className="text-black text-[13px] leading-[1.8] max-w-[380px] lg:text-right"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Voer uw kenteken in en ontdek direct welke prestatieverbetering wij voor uw voertuig kunnen realiseren.
          </p>
        </motion.div>

        {/* License Plate + Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="max-w-full md:max-w-[520px] mb-16"
        >
          <div
            className="relative mb-4"
            style={{
              background: 'linear-gradient(180deg, #FFDE37 0%, #F7C748 50%, #E8B830 100%)',
              borderRadius: '14px',
              padding: '3px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.3)',
            }}
          >
            <div className="flex h-[66px] rounded-[12px] overflow-hidden" style={{ border: '1.5px solid #c9a020' }}>
              <div
                className="w-[44px] flex flex-col items-center justify-center shrink-0 gap-0.5"
                style={{ background: 'linear-gradient(180deg, #003DA5 0%, #002B7A 100%)' }}
              >
                <svg width="18" height="12" viewBox="0 0 18 12" fill="none">
                  <circle cx="9" cy="6" r="4.5" stroke="#F7C748" strokeWidth="0.8" fill="none" />
                  {[...Array(12)].map((_, i) => {
                    const angle = (i * 30 * Math.PI) / 180
                    return <circle key={i} cx={9 + 3.5 * Math.cos(angle)} cy={6 + 3.5 * Math.sin(angle)} r="0.4" fill="#F7C748" />
                  })}
                </svg>
                <span className="text-white text-[8px] font-bold tracking-wider leading-none">NL</span>
              </div>
              <input
                type="text"
                value={plate}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Kenteken"
                className="flex-1 bg-transparent text-[#1a1400] text-center text-[28px] font-extrabold uppercase tracking-[0.12em] placeholder:text-[#1a1400]/20 placeholder:text-lg placeholder:font-medium placeholder:tracking-[0.05em] outline-none px-4"
                style={{ fontFamily: "'Kenteken', 'License Plate', 'Manrope', sans-serif" }}
                maxLength={9}
              />
            </div>
          </div>
          <motion.button
            onClick={handleLookup}
            disabled={loading || plate.replace(/-/g, '').length < 5}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            className="w-full h-[52px] bg-black text-white text-xs tracking-[0.1em] uppercase font-medium rounded-full disabled:opacity-20 disabled:cursor-not-allowed transition-colors hover:bg-black/85"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Zoeken...
              </span>
            ) : 'Bereken vermogenswinst'}
          </motion.button>
        </motion.div>

        {/* Error */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mb-10 bg-red-50 border border-red-200 rounded-2xl px-6 py-4"
            >
              <p className="text-red-600 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results */}
        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              {/* Vehicle Info Card */}
              <div className="bg-white rounded-2xl p-8 md:p-10 mb-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                  <div>
                    <p
                      className="text-black text-[11px] uppercase tracking-[0.2em] font-medium mb-2"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      Voertuig herkend
                    </p>
                    <h3
                      className="text-black text-2xl md:text-3xl font-medium tracking-[0.04em] uppercase"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {result.vehicle.brand} {result.vehicle.model}
                    </h3>
                  </div>
                  <div
                    className="flex flex-wrap gap-3"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {result.vehicle.year && (
                      <span className="bg-[#f2f2f0] text-black text-[11px] uppercase tracking-[0.1em] font-medium px-4 py-2 rounded-full">
                        {result.vehicle.year}
                      </span>
                    )}
                    <span className="bg-[#f2f2f0] text-black text-[11px] uppercase tracking-[0.1em] font-medium px-4 py-2 rounded-full">
                      {result.vehicle.isHybrid ? 'Hybride' : result.vehicle.fuelType}
                    </span>
                    {(result.vehicle.combinedPowerHp || result.vehicle.powerHp) && (
                      <span className="bg-[#f2f2f0] text-black text-[11px] uppercase tracking-[0.1em] font-medium px-4 py-2 rounded-full">
                        {result.vehicle.combinedPowerHp || result.vehicle.powerHp} PK{result.vehicle.isHybrid ? ' (systeem)' : ''}
                      </span>
                    )}
                    {result.vehicle.displacementCc && (
                      <span className="bg-[#f2f2f0] text-black text-[11px] uppercase tracking-[0.1em] font-medium px-4 py-2 rounded-full">
                        {(result.vehicle.displacementCc / 1000).toFixed(1)}L
                      </span>
                    )}
                    <span className="bg-[#f2f2f0] text-black text-[11px] uppercase tracking-[0.1em] font-medium px-4 py-2 rounded-full">
                      {result.vehicle.licensePlate}
                    </span>
                  </div>
                </div>
              </div>

              {/* Variant Selector */}
              {result.engineVariants.length > 1 && (
                <div className="bg-white rounded-2xl p-8 md:p-10 mb-6">
                  <p
                    className="text-black text-[11px] uppercase tracking-[0.2em] font-medium mb-5"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Selecteer uw motorvariant
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {result.engineVariants.map(v => (
                      <button
                        key={v.id}
                        onClick={() => handleVariantSelect(v)}
                        className={`px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.08em] font-medium transition-all ${
                          selectedVariant?.id === v.id
                            ? 'bg-black text-white'
                            : 'bg-[#f2f2f0] text-black hover:bg-black/5'
                        }`}
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      >
                        {v.engineName} · {v.originalHp} PK
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* No Data State */}
              {result.engineVariants.length === 0 && (
                <div className="bg-white rounded-2xl p-8 md:p-10 text-center">
                  <div className="w-14 h-14 rounded-full bg-[#f2f2f0] flex items-center justify-center mx-auto mb-5">
                    <svg className="w-7 h-7 text-[#B1A06D]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h3
                    className="text-black text-lg font-medium tracking-[0.04em] uppercase mb-3"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Niet direct beschikbaar
                  </h3>
                  <p className="text-black/60 text-sm mb-6 max-w-[360px] mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    Voor uw {result.vehicle.brand} {result.vehicle.model} hebben wij nog geen standaard chiptuning data beschikbaar. Neem contact met ons op zodat we samen de mogelijkheden voor uw voertuig kunnen bespreken.
                  </p>
                  <button
                    onClick={() => { setContactSuccess(false); setContactForm({ name: '', phone: '', email: '', message: '' }); setContactOpen(true); }}
                    className="bg-black text-white text-xs tracking-[0.1em] uppercase px-8 py-3.5 rounded-full font-medium hover:bg-black/85 transition-colors"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Neem contact op
                  </button>
                </div>
              )}

              {/* Performance Results */}
              <AnimatePresence mode="wait">
                {selectedVariant && result.stages.length > 0 && (
                  <motion.div
                    key={selectedVariant.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4 }}
                    className="bg-white rounded-2xl p-8 md:p-10"
                  >
                    {/* Stage Tabs */}
                    {result.stages.length > 1 && (
                      <div className="flex gap-2 mb-10">
                        {result.stages.map((stage, i) => (
                          <button
                            key={stage.id}
                            onClick={() => setSelectedStageIndex(i)}
                            className={`px-6 py-2.5 rounded-full text-[11px] font-medium uppercase tracking-[0.1em] transition-all ${
                              selectedStageIndex === i
                                ? 'bg-black text-white'
                                : 'bg-[#f2f2f0] text-black hover:bg-black/5'
                            }`}
                            style={{ fontFamily: "'Manrope', sans-serif" }}
                          >
                            {stage.stage}
                          </button>
                        ))}
                      </div>
                    )}

                    {currentStage && (
                      <motion.div
                        key={currentStage.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        {/* Big Numbers */}
                        <div className="grid grid-cols-2 gap-4 md:gap-6 mb-10">
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 }}
                            className="bg-[#f2f2f0] rounded-2xl p-6 md:p-8"
                          >
                            <p
                              className="text-black text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight"
                              style={{ fontFamily: "'Manrope', sans-serif" }}
                            >
                              +{currentStage.hpGain}
                            </p>
                            <p
                              className="text-black text-[11px] uppercase tracking-[0.2em] mt-3 font-medium"
                              style={{ fontFamily: "'Manrope', sans-serif" }}
                            >
                              PK winst
                            </p>
                          </motion.div>
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="bg-[#f2f2f0] rounded-2xl p-6 md:p-8"
                          >
                            <p
                              className="text-black text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight"
                              style={{ fontFamily: "'Manrope', sans-serif" }}
                            >
                              +{currentStage.nmGain}
                            </p>
                            <p
                              className="text-black text-[11px] uppercase tracking-[0.2em] mt-3 font-medium"
                              style={{ fontFamily: "'Manrope', sans-serif" }}
                            >
                              NM winst
                            </p>
                          </motion.div>
                        </div>

                        {/* Detailed Bars */}
                        <div className="space-y-8 mb-10">
                          <div>
                            <div className="flex justify-between items-baseline mb-3">
                              <span
                                className="text-black text-[11px] uppercase tracking-[0.2em] font-medium"
                                style={{ fontFamily: "'Manrope', sans-serif" }}
                              >
                                Vermogen
                              </span>
                              <div className="flex items-baseline gap-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                                <span className="text-black/40 text-sm line-through">{selectedVariant.originalHp} PK</span>
                                <span className="text-black text-lg font-medium">{currentStage.tunedHp} PK</span>
                              </div>
                            </div>
                            <div className="relative h-1 bg-black/5 rounded-full overflow-hidden">
                              <motion.div
                                className="absolute inset-y-0 left-0 bg-black/10 rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.min((selectedVariant.originalHp / (selectedVariant.originalHp * 1.6)) * 100, 100)}%` }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                              />
                              <motion.div
                                className="absolute inset-y-0 left-0 bg-black rounded-full"
                                initial={{ width: 0 }}
                                animate={{ width: `${Math.min((currentStage.tunedHp / (selectedVariant.originalHp * 1.6)) * 100, 100)}%` }}
                                transition={{ duration: 1, delay: 0.4 }}
                              />
                            </div>
                          </div>

                          {selectedVariant.originalNm && currentStage.tunedNm && (
                            <div>
                              <div className="flex justify-between items-baseline mb-3">
                                <span
                                  className="text-black text-[11px] uppercase tracking-[0.2em] font-medium"
                                  style={{ fontFamily: "'Manrope', sans-serif" }}
                                >
                                  Koppel
                                </span>
                                <div className="flex items-baseline gap-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
                                  <span className="text-black/40 text-sm line-through">{selectedVariant.originalNm} NM</span>
                                  <span className="text-black text-lg font-medium">{currentStage.tunedNm} NM</span>
                                </div>
                              </div>
                              <div className="relative h-1 bg-black/5 rounded-full overflow-hidden">
                                <motion.div
                                  className="absolute inset-y-0 left-0 bg-black/10 rounded-full"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min((selectedVariant.originalNm / (selectedVariant.originalNm * 1.6)) * 100, 100)}%` }}
                                  transition={{ duration: 0.8, delay: 0.3 }}
                                />
                                <motion.div
                                  className="absolute inset-y-0 left-0 bg-[#B1A06D] rounded-full"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min((currentStage.tunedNm / (selectedVariant.originalNm * 1.6)) * 100, 100)}%` }}
                                  transition={{ duration: 1, delay: 0.5 }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Features */}
                        {currentStage.features.length > 0 && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                            className="border-t border-black/5 pt-8 mb-10"
                          >
                            <p
                              className="text-black text-[11px] uppercase tracking-[0.2em] font-medium mb-4"
                              style={{ fontFamily: "'Manrope', sans-serif" }}
                            >
                              Inclusief
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {currentStage.features.map((f, i) => (
                                <span
                                  key={i}
                                  className="flex items-center gap-2 px-4 py-2 bg-[#f2f2f0] rounded-full text-black text-[11px] tracking-[0.05em] font-medium"
                                  style={{ fontFamily: "'Manrope', sans-serif" }}
                                >
                                  <span className="w-1 h-1 rounded-full bg-[#B1A06D]" />
                                  {f}
                                </span>
                              ))}
                            </div>
                          </motion.div>
                        )}

                        {/* CTA */}
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
                        >
                          <button
                            onClick={() => { setContactSuccess(false); setContactForm({ name: '', phone: '', email: '', message: '' }); setContactOpen(true); }}
                            className="bg-black text-white text-xs tracking-[0.1em] uppercase px-8 py-4 rounded-full flex items-center gap-2 hover:bg-black/85 transition-colors font-medium"
                            style={{ fontFamily: "'Manrope', sans-serif" }}
                          >
                            Afspraak aanvragen
                          </button>
                          <span
                            className="text-black text-[11px] tracking-wider"
                            style={{ fontFamily: "'Manrope', sans-serif" }}
                          >
                            Vrijblijvend advies op maat
                          </span>
                        </motion.div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Empty State */}
        {!result && !error && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-10 md:p-16 flex flex-col items-center text-center"
          >
            <div className="w-16 h-16 rounded-full bg-[#f2f2f0] flex items-center justify-center mb-6">
              <svg className="w-7 h-7 text-black/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                <path d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
              </svg>
            </div>
            <p
              className="text-black text-[13px] max-w-[280px]"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Voer uw kenteken in om de mogelijke vermogenswinst te berekenen.
            </p>
          </motion.div>
        )}

      </div>

      {/* Contact Dialog */}
      <AnimatePresence>
        {contactOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setContactOpen(false)}
            />

            {/* Modal */}
            <motion.div
              className="relative bg-white rounded-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto p-8 md:p-10"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Close */}
              <button
                onClick={() => setContactOpen(false)}
                className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center text-black/30 hover:text-black transition-colors"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>

              {contactSuccess ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center text-center py-8"
                >
                  <div className="w-14 h-14 bg-[#B1A06D] rounded-full flex items-center justify-center mb-5">
                    <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3
                    className="text-black text-xl font-semibold mb-2"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Aanvraag verzonden!
                  </h3>
                  <p
                    className="text-black/50 text-sm mb-6"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Wij nemen zo snel mogelijk contact met u op over de chiptuning voor uw {result?.vehicle.brand} {result?.vehicle.model}.
                  </p>
                  <button
                    onClick={() => setContactOpen(false)}
                    className="bg-black text-white text-xs tracking-[0.1em] uppercase px-8 py-3.5 rounded-full font-medium hover:bg-black/85 transition-colors"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Sluiten
                  </button>
                </motion.div>
              ) : (
                <>
                  <p
                    className="text-black text-[11px] uppercase tracking-[0.2em] font-medium mb-2"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Chiptuning aanvraag
                  </p>
                  <h3
                    className="text-black text-2xl font-medium tracking-[0.04em] uppercase mb-6"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Afspraak aanvragen
                  </h3>

                  {/* Vehicle summary */}
                  {result && currentStage && (
                    <div className="bg-[#f2f2f0] rounded-xl p-5 mb-6">
                      <div className="flex items-center justify-between mb-2">
                        <span
                          className="text-black font-medium text-sm uppercase tracking-[0.04em]"
                          style={{ fontFamily: "'Manrope', sans-serif" }}
                        >
                          {result.vehicle.brand} {result.vehicle.model}
                        </span>
                        <span
                          className="text-black text-[11px] uppercase tracking-[0.1em] font-medium bg-white px-3 py-1 rounded-full"
                          style={{ fontFamily: "'Manrope', sans-serif" }}
                        >
                          {result.vehicle.licensePlate}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 text-[11px]" style={{ fontFamily: "'Manrope', sans-serif" }}>
                        <span className="text-black/60">{currentStage.stage}</span>
                        <span className="text-black/30">·</span>
                        <span className="text-black/60">{selectedVariant?.originalHp}pk → {currentStage.tunedHp}pk</span>
                        <span className="text-black/30">·</span>
                        <span className="text-black/60">+{currentStage.hpGain} PK</span>
                        {currentStage.nmGain > 0 && (
                          <>
                            <span className="text-black/30">·</span>
                            <span className="text-black/60">+{currentStage.nmGain} NM</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Form */}
                  <form onSubmit={handleContactSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        placeholder="Uw naam *"
                        value={contactForm.name}
                        onChange={e => setContactForm(f => ({ ...f, name: e.target.value }))}
                        required
                        className="w-full bg-[#f2f2f0] text-black text-sm placeholder:text-black/35 px-4 py-3.5 rounded-xl outline-none focus:ring-2 focus:ring-[#B1A06D]/30 transition-shadow"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      />
                      <input
                        type="tel"
                        placeholder="Telefoonnummer *"
                        value={contactForm.phone}
                        onChange={e => setContactForm(f => ({ ...f, phone: e.target.value }))}
                        required
                        className="w-full bg-[#f2f2f0] text-black text-sm placeholder:text-black/35 px-4 py-3.5 rounded-xl outline-none focus:ring-2 focus:ring-[#B1A06D]/30 transition-shadow"
                        style={{ fontFamily: "'Manrope', sans-serif" }}
                      />
                    </div>
                    <input
                      type="email"
                      placeholder="E-mailadres"
                      value={contactForm.email}
                      onChange={e => setContactForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full bg-[#f2f2f0] text-black text-sm placeholder:text-black/35 px-4 py-3.5 rounded-xl outline-none focus:ring-2 focus:ring-[#B1A06D]/30 transition-shadow"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    />
                    <textarea
                      placeholder="Eventuele opmerkingen of wensen"
                      value={contactForm.message}
                      onChange={e => setContactForm(f => ({ ...f, message: e.target.value }))}
                      rows={3}
                      className="w-full bg-[#f2f2f0] text-black text-sm placeholder:text-black/35 px-4 py-3.5 rounded-xl outline-none focus:ring-2 focus:ring-[#B1A06D]/30 transition-shadow resize-none"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    />
                    <button
                      type="submit"
                      disabled={contactSubmitting}
                      className="w-full bg-black text-white text-xs tracking-[0.1em] uppercase py-4 rounded-full font-medium hover:bg-black/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed mt-2"
                      style={{ fontFamily: "'Manrope', sans-serif" }}
                    >
                      {contactSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Verzenden...
                        </span>
                      ) : 'Aanvraag versturen'}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default ChiptuningLookup
